import { useCallback, useMemo } from 'react';

import { WRAPPER_TO_PROVIDER_EVENT_TYPES } from '@/features/event-handlers/wrapper-event-handlers/provider-events/types';
import { Provider } from '@/features/game-context/types';
import { handleUnknownEvent } from '@/features/game-window-integration/helpers/generic-helpers';
import { errorsRefs } from '@/features/game-window-integration/providers/aristocrat/error-helpers';
import {
  AristocratCommandType,
  AristocratEventData,
  AristocratEventHandlerMap,
} from '@/features/game-window-integration/providers/aristocrat/types';
import useAristocratHandlers from '@/features/game-window-integration/providers/aristocrat/use-aristocrat-handlers';
import { AristocratEvents } from '@/features/game-window-integration/types';
import {
  DdEventCategory,
  DdEventType,
  Logger,
} from '@/features/logging/logger';
import { WrapperProxy } from '@/features/wrapper-proxy/wrapper-proxy';

export const useAristocratEventHandlers = () => {
  const {
    handleGameReady,
    handleSpinInProgress,
    handleShowDeposit,
    handleVolumeChange,
    handleGameReset,
    handleError,
    gameHandler,
  } = useAristocratHandlers();

  const eventHandlerMapper: AristocratEventHandlerMap = useMemo(
    () => ({
      [AristocratEvents.APPLICATION_READY]: handleGameReady,
      [AristocratEvents.SPIN_START]: handleSpinInProgress,
      [AristocratEvents.SPIN_END]: handleSpinInProgress,
      [AristocratEvents.SOUND_OFF]: handleVolumeChange,
      [AristocratEvents.SOUND_ON]: handleVolumeChange,
      [AristocratEvents.SHOW_DEPOSIT]: handleShowDeposit,
      [AristocratEvents.RESET_GAME]: handleGameReset,
      [AristocratEvents.ERROR]: handleError,
    }),
    [
      handleSpinInProgress,
      handleGameReady,
      handleVolumeChange,
      handleShowDeposit,
      handleGameReset,
      handleError,
    ],
  );

  const providerEventHandler = useCallback(
    (event: MessageEvent) => {
      const parsedEventData: AristocratEventData = typeof event?.data ===
        'string' && {
        ...JSON.parse(event.data),
      };

      const newEventObject = {
        rawEvent: event,
        ...(parsedEventData && { data: parsedEventData }),
      };

      const eventType = newEventObject?.data?.event;
      const isErrorEvent =
        eventType && Object.values(errorsRefs).includes(eventType);

      if (isErrorEvent) {
        eventHandlerMapper[AristocratEvents.ERROR]?.(newEventObject);
      } else if (eventType) {
        eventHandlerMapper[eventType]?.(newEventObject);
      } else {
        handleUnknownEvent(newEventObject);
      }
    },
    [eventHandlerMapper],
  );

  const wrapperEventHandler = useCallback(
    (e: MessageEvent) => {
      const executeEvent = (event: AristocratCommandType) => {
        if (gameHandler) {
          const eventJson = JSON.stringify(event);
          const options = { targetOrigin: '*' };
          gameHandler.postMessage(eventJson, options);
        } else {
          Logger.rumError({
            name: 'Failed to get handler for provider',
            useCase: DdEventCategory.ProviderIntegration,
            eventType: DdEventType.Error,
            context: {
              provider: Provider.ARISTOCRAT,
            },
          });
        }
      };

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.TOGGLE_AUDIO) {
        const audioToggleValue = {
          event: e?.data?.payload
            ? AristocratEvents.SOUND_ON
            : AristocratEvents.SOUND_OFF,
        };
        executeEvent(audioToggleValue as unknown as AristocratCommandType);
        WrapperProxy.setAudio(e.data.payload);
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.RELOAD_BALANCE) {
        executeEvent({
          event: AristocratEvents.RELOAD_BALANCE,
          payload: {
            balance: e.data.payload,
          },
        });
      }

      if (e.data.type === WRAPPER_TO_PROVIDER_EVENT_TYPES.AUTO_SPIN_STOP) {
        executeEvent({
          event: AristocratEvents.AUTO_SPIN_STOP,
        });
      }
    },
    [gameHandler],
  );

  return { wrapperEventHandler, providerEventHandler };
};
